import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '../utils/api';
import { toast } from 'sonner';
import { Mail, Eye, Save, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = getBackendUrl();

const AdminEmailTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editData, setEditData] = useState({ subject: '', html_content: '' });
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await fetch(`${API}/admin/email-templates`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setTemplates(data.templates);
        if (data.templates.length > 0 && !selectedTemplate) {
          selectTemplate(data.templates[0]);
        }
      }
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const selectTemplate = async (template) => {
    setSelectedTemplate(template);
    setEditData({
      subject: template.subject || '',
      html_content: template.html_content || ''
    });
    
    // Auto-load preview to show current/default template
    await loadPreview(template.template_type);
  };

  const loadPreview = async (templateType) => {
    setLoadingPreview(true);
    try {
      const response = await fetch(`${API}/admin/email-templates/${templateType}/preview`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setPreviewHtml(data.preview.html_content);
        setPreviewSubject(data.preview.subject);
      }
    } catch (error) {
      console.error('Failed to load preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/email-templates/${selectedTemplate.template_type}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editData)
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Template saved successfully');
        fetchTemplates();
        // Reload preview after save
        await loadPreview(selectedTemplate.template_type);
      } else {
        toast.error(data.detail || 'Failed to save template');
      }
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const getTemplateTypeLabel = (type) => {
    const labels = {
      'booking_confirmation': 'Booking Confirmation',
      'payment_reminder': 'Payment Reminder',
      'event_reminder': 'Event Reminder (1 Week)',
      'cancellation': 'Booking Cancellation',
      'waitlist': 'Waitlist Notification',
      'bulk': 'Bulk Email'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-[#1a1f16] min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#c5bfa9]">Email Templates</h1>
        <p className="text-[#8b9a7e] mt-1">Customize email templates sent to customers</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Template List */}
        <div className="lg:col-span-1 bg-[#232921] rounded-lg border border-[#3d4a35] p-4">
          <h2 className="text-sm font-semibold text-[#8b9a7e] uppercase tracking-wider mb-4">Templates</h2>
          <div className="space-y-2">
            {templates.map((template) => (
              <button
                key={template.id}
                onClick={() => selectTemplate(template)}
                className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                  selectedTemplate?.id === template.id
                    ? 'bg-[#3d4a35] text-[#c5bfa9]'
                    : 'text-[#8b9a7e] hover:bg-[#2d3728]'
                }`}
              >
                <div className="flex items-center">
                  <Mail className="w-4 h-4 mr-2" />
                  <span className="text-sm">{template.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Editor & Preview */}
        <div className="lg:col-span-3 space-y-4">
          {selectedTemplate ? (
            <>
              {/* Current Email Preview */}
              <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-[#c5bfa9]">
                      Current Email Preview
                    </h2>
                    <p className="text-sm text-[#6b7a5e]">
                      {editData.html_content ? 'Using custom template' : 'Using system default template'}
                    </p>
                  </div>
                  <Button
                    onClick={() => loadPreview(selectedTemplate.template_type)}
                    disabled={loadingPreview}
                    variant="outline"
                    size="sm"
                    className="border-[#3d4a35] text-[#8b9a7e] hover:bg-[#2d3728]"
                  >
                    {loadingPreview ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                    <span className="ml-2">Refresh</span>
                  </Button>
                </div>
                
                {previewSubject && (
                  <div className="mb-3 p-2 bg-[#1a1f16] rounded border border-[#3d4a35]">
                    <span className="text-xs text-[#6b7a5e]">Subject: </span>
                    <span className="text-sm text-[#c5bfa9]">{previewSubject}</span>
                  </div>
                )}
                
                {loadingPreview ? (
                  <div className="flex items-center justify-center h-48 bg-[#1a1f16] rounded-lg">
                    <div className="w-6 h-6 border-2 border-[#5a6f4e] border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : previewHtml ? (
                  <div 
                    className="bg-white rounded-lg p-4 max-h-[400px] overflow-auto"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                ) : (
                  <div className="flex items-center justify-center h-48 bg-[#1a1f16] rounded-lg text-[#6b7a5e]">
                    No preview available
                  </div>
                )}
              </div>

              {/* Editor */}
              <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-[#c5bfa9]">
                    Customize: {getTemplateTypeLabel(selectedTemplate.template_type)}
                  </h2>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#4a5d42] hover:bg-[#5a6f4e] text-white"
                  >
                    {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save
                  </Button>
                </div>

                {/* Available Placeholders */}
                <div className="mb-4 p-3 bg-[#1a1f16] rounded-lg border border-[#3d4a35]">
                  <p className="text-xs text-[#8b9a7e] mb-2">Available placeholders (click to copy):</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedTemplate.placeholders?.map((placeholder) => (
                      <code 
                        key={placeholder}
                        className="px-2 py-1 bg-[#2d3728] text-[#c5bfa9] text-xs rounded cursor-pointer hover:bg-[#3d4a35]"
                        onClick={() => {
                          navigator.clipboard.writeText(`{{${placeholder}}}`);
                          toast.success(`Copied {{${placeholder}}}`);
                        }}
                      >
                        {`{{${placeholder}}}`}
                      </code>
                    ))}
                  </div>
                </div>

                {/* Subject */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#8b9a7e] mb-2">Subject Line</label>
                  <input
                    type="text"
                    value={editData.subject}
                    onChange={(e) => setEditData({ ...editData, subject: e.target.value })}
                    className="w-full px-4 py-2 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] focus:border-[#5a6f4e] focus:outline-none"
                    placeholder="Email subject..."
                  />
                </div>

                {/* HTML Content */}
                <div>
                  <label className="block text-sm font-medium text-[#8b9a7e] mb-2">
                    Custom HTML Content <span className="text-xs text-[#6b7a5e]">(Leave empty to use system default shown above)</span>
                  </label>
                  <textarea
                    value={editData.html_content}
                    onChange={(e) => setEditData({ ...editData, html_content: e.target.value })}
                    rows={12}
                    className="w-full px-4 py-3 bg-[#1a1f16] border border-[#3d4a35] rounded-lg text-[#c5bfa9] font-mono text-sm focus:border-[#5a6f4e] focus:outline-none"
                    placeholder="Enter custom HTML content or leave empty to use system default..."
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="bg-[#232921] rounded-lg border border-[#3d4a35] p-6 text-center">
              <p className="text-[#8b9a7e]">Select a template to edit</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplates;
